import React from 'react';
import { ArrowRight, Heart, Star, Truck, RefreshCw, Shield, Minus, Plus, Instagram } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 10: MONO
 * Style: Ultra-minimal, Clean, Typography-focused
 * Best for: Contemporary basics, Minimalist Indian wear, Premium essentials
 * Pure white theme with subtle gray accents
 */
const Template10 = () => {
    const navigate = useNavigate();

    // Categories - Minimal basics
    const categories = [
        { name: 'Kurtas', count: '48', image: 'https://placehold.co/600x800/F9FAFB/374151?text=Kurtas' },
        { name: 'Shirts', count: '36', image: 'https://placehold.co/600x800/F3F4F6/374151?text=Shirts' },
        { name: 'Linen', count: '24', image: 'https://placehold.co/600x800/E5E7EB/374151?text=Linen' }
    ];

    // Products - Minimalist Indian fashion
    const products = [
        { id: 1, name: 'Classic Cotton Kurta', price: '₹2,490', image: 'https://placehold.co/400x500/F9FAFB/374151?text=Kurta+1' },
        { id: 2, name: 'Linen Blend Shirt', price: '₹1,890', image: 'https://placehold.co/400x500/F3F4F6/374151?text=Shirt+1' },
        { id: 3, name: 'Pure Cotton Palazzo', price: '₹1,690', image: 'https://placehold.co/400x500/E5E7EB/374151?text=Palazzo+1' },
        { id: 4, name: 'Minimal Kurta Set', price: '₹3,290', image: 'https://placehold.co/400x500/F9FAFB/374151?text=Set+1' },
        { id: 5, name: 'Handloom Cotton Saree', price: '₹4,990', image: 'https://placehold.co/400x500/F3F4F6/374151?text=Saree+1' },
        { id: 6, name: 'Organic Linen Dress', price: '₹2,990', image: 'https://placehold.co/400x500/E5E7EB/374151?text=Dress+1' },
        { id: 7, name: 'Essential White Kurta', price: '₹1,990', image: 'https://placehold.co/400x500/F9FAFB/374151?text=Kurta+2' },
        { id: 8, name: 'Relaxed Fit Pants', price: '₹1,490', image: 'https://placehold.co/400x500/F3F4F6/374151?text=Pants+1' }
    ];

    // Testimonials
    const testimonials = [
        { text: 'Finally, a brand that believes in less is more. The quality speaks for itself.', name: 'Aditi Sharma', location: 'Mumbai' },
        { text: 'Perfect everyday wear. Simple, comfortable, and beautifully made.', name: 'Vikram Iyer', location: 'Bangalore' }
    ];

    return (
        <div className="w-full min-h-screen bg-white font-sans">
            {/* Hero Section - Ultra Minimal */}
            <section className="min-h-[90vh] flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-4xl mx-auto text-center">
                    <p className="text-gray-400 text-sm tracking-[0.3em] uppercase mb-8">
                        Mindful Essentials
                    </p>
                    <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-extralight text-gray-900 leading-[1.0] mb-8 tracking-tight">
                        Less,
                        <br />
                        but better.
                    </h1>
                    <p className="text-gray-500 text-lg lg:text-xl max-w-xl mx-auto mb-12 leading-relaxed font-light">
                        Thoughtfully designed clothing that simplifies your wardrobe.
                        Quality over quantity. Always.
                    </p>
                    <button
                        onClick={() => navigate('/template10/shop')}
                        className="group inline-flex items-center gap-4 text-gray-900 text-sm tracking-[0.2em] uppercase hover:gap-6 transition-all duration-300"
                    >
                        <span>Explore Collection</span>
                        <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </section>

            {/* Featured Image */}
            <section className="px-4 sm:px-6 lg:px-8 pb-24">
                <div className="max-w-6xl mx-auto">
                    <div className="aspect-[16/9] lg:aspect-[21/9] overflow-hidden bg-gray-50">
                        <img
                            src="https://placehold.co/1400x600/F9FAFB/374151?text=Minimal+Collection"
                            alt="Collection"
                            className="w-full h-full object-cover"
                        />
                    </div>
                </div>
            </section>

            {/* Philosophy Section */}
            <section className="py-24 lg:py-32 bg-gray-50">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <p className="text-gray-400 text-sm tracking-[0.3em] uppercase mb-6">Our Philosophy</p>
                    <h2 className="text-3xl lg:text-4xl font-extralight text-gray-900 mb-8 leading-relaxed">
                        We believe in the power of simplicity.
                        Clothes that work harder so you don't have to think about them.
                    </h2>
                    <div className="h-px w-16 bg-gray-300 mx-auto" />
                </div>
            </section>

            {/* Categories */}
            <section className="py-24 lg:py-32 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-end mb-16">
                        <div>
                            <p className="text-gray-400 text-sm tracking-[0.3em] uppercase mb-3">Shop By</p>
                            <h2 className="text-3xl lg:text-4xl font-extralight text-gray-900">Category</h2>
                        </div>
                        <button
                            onClick={() => navigate('/template10/shop')}
                            className="group flex items-center gap-3 text-gray-500 text-sm tracking-wider uppercase hover:text-gray-900 transition-colors"
                        >
                            View All
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                        {categories.map((category, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template10/shop')}
                                className="group cursor-pointer"
                            >
                                <div className="aspect-[3/4] overflow-hidden bg-gray-100 mb-6">
                                    <img
                                        src={category.image}
                                        alt={category.name}
                                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                                    />
                                </div>
                                <div className="flex justify-between items-baseline">
                                    <h3 className="text-gray-900 text-lg font-light">{category.name}</h3>
                                    <span className="text-gray-400 text-sm">{category.count} items</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Products */}
            <section className="py-24 lg:py-32 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-end mb-16">
                        <div>
                            <p className="text-gray-400 text-sm tracking-[0.3em] uppercase mb-3">The Edit</p>
                            <h2 className="text-3xl lg:text-4xl font-extralight text-gray-900">New Arrivals</h2>
                        </div>
                        <button
                            onClick={() => navigate('/template10/shop')}
                            className="group flex items-center gap-3 text-gray-500 text-sm tracking-wider uppercase hover:text-gray-900 transition-colors"
                        >
                            Shop All
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-12 lg:gap-x-6 lg:gap-y-16">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate('/template10/shop')}
                                className="group cursor-pointer"
                            >
                                <div className="relative aspect-[3/4] overflow-hidden bg-gray-100 mb-4">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                                    />
                                    <button className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                        <Heart className="w-5 h-5 text-gray-600 hover:text-gray-900" />
                                    </button>
                                </div>
                                <h3 className="text-gray-900 font-light mb-1 group-hover:underline underline-offset-4 transition-all">
                                    {product.name}
                                </h3>
                                <p className="text-gray-500 text-sm">{product.price}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Quote Section */}
            <section className="py-24 lg:py-32 bg-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <blockquote className="text-2xl lg:text-3xl font-extralight text-gray-900 leading-relaxed mb-8">
                        "The ability to simplify means to eliminate the unnecessary
                        so that the necessary may speak."
                    </blockquote>
                    <cite className="text-gray-400 text-sm tracking-wider uppercase not-italic">
                        — Hans Hofmann
                    </cite>
                </div>
            </section>

            {/* Materials Section */}
            <section className="py-24 lg:py-32 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
                        <div>
                            <p className="text-gray-400 text-sm tracking-[0.3em] uppercase mb-6">Our Materials</p>
                            <h2 className="text-3xl lg:text-4xl font-extralight text-gray-900 mb-8">
                                Honest fabrics,
                                <br />
                                no compromises.
                            </h2>
                            <p className="text-gray-600 leading-relaxed mb-8">
                                We source only natural, breathable fabrics. Pure cotton from Maharashtra,
                                handwoven linen from Bengal, and organic khadi from Gujarat.
                                Each piece is made to last, designed to age beautifully.
                            </p>
                            <div className="space-y-4">
                                {['100% Natural Fibers', 'OEKO-TEX Certified', 'Handcrafted in India', 'Zero Synthetic Blends'].map((item, index) => (
                                    <div key={index} className="flex items-center gap-4">
                                        <div className="w-1 h-1 bg-gray-400 rounded-full" />
                                        <span className="text-gray-700 text-sm">{item}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="aspect-[3/4] overflow-hidden bg-gray-100">
                                <img
                                    src="https://placehold.co/400x500/F9FAFB/374151?text=Fabric+1"
                                    alt="Fabric"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="aspect-[3/4] overflow-hidden bg-gray-100 mt-8">
                                <img
                                    src="https://placehold.co/400x500/F3F4F6/374151?text=Fabric+2"
                                    alt="Fabric"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-24 lg:py-32 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <p className="text-gray-400 text-sm tracking-[0.3em] uppercase mb-16 text-center">What People Say</p>
                    <div className="grid md:grid-cols-2 gap-16 lg:gap-24">
                        {testimonials.map((testimonial, index) => (
                            <div key={index} className="text-center">
                                <p className="text-xl lg:text-2xl font-extralight text-gray-900 mb-8 leading-relaxed">
                                    "{testimonial.text}"
                                </p>
                                <div>
                                    <p className="text-gray-900 text-sm font-medium">{testimonial.name}</p>
                                    <p className="text-gray-400 text-sm">{testimonial.location}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Newsletter */}
            <section className="py-24 lg:py-32 bg-gray-900 text-white">
                <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <p className="text-gray-500 text-sm tracking-[0.3em] uppercase mb-6">Newsletter</p>
                    <h2 className="text-3xl lg:text-4xl font-extralight text-white mb-6">
                        Stay in the loop
                    </h2>
                    <p className="text-gray-400 mb-10">
                        New arrivals. Behind the scenes. No spam.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                        <input
                            type="email"
                            placeholder="Your email"
                            className="flex-1 px-6 py-4 bg-transparent border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:border-gray-500 transition-colors"
                        />
                        <button className="px-8 py-4 bg-white text-gray-900 text-sm tracking-wider uppercase hover:bg-gray-100 transition-colors">
                            Join
                        </button>
                    </div>
                </div>
            </section>

            {/* Instagram */}
            <section className="py-24 lg:py-32 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <div className="flex items-center justify-center gap-3 mb-3">
                            <Instagram className="w-5 h-5 text-gray-400" />
                            <span className="text-gray-400 text-sm tracking-wider">@mono.essentials</span>
                        </div>
                    </div>

                    <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                        {[1, 2, 3, 4, 5, 6].map((_, index) => (
                            <div key={index} className="aspect-square overflow-hidden bg-gray-100 cursor-pointer">
                                <img
                                    src={`https://placehold.co/300x300/F3F4F6/374151?text=${index + 1}`}
                                    alt={`Instagram ${index + 1}`}
                                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services */}
            <section className="py-16 border-t border-gray-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
                        <div>
                            <Truck className="w-5 h-5 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-900 text-sm font-medium mb-1">Free Shipping</p>
                            <p className="text-gray-400 text-xs">Orders above ₹1,499</p>
                        </div>
                        <div>
                            <RefreshCw className="w-5 h-5 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-900 text-sm font-medium mb-1">Easy Returns</p>
                            <p className="text-gray-400 text-xs">30-day return window</p>
                        </div>
                        <div>
                            <Shield className="w-5 h-5 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-900 text-sm font-medium mb-1">Quality Guarantee</p>
                            <p className="text-gray-400 text-xs">Lifetime warranty</p>
                        </div>
                        <div>
                            <Heart className="w-5 h-5 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-900 text-sm font-medium mb-1">Made with Care</p>
                            <p className="text-gray-400 text-xs">Ethical production</p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Template10;
